import { useMutation, useQueryClient } from '@tanstack/react-query'

import type { AccountIdentity } from '@/bindings/AccountIdentity'
import { ConfirmButton } from '@/components/confirm'
import { FormError } from '@/components/form-error'
import { accountName } from '@/features/back-office/account-name'
import { request } from '@/lib/api'
import type { MessageKey } from '@/lib/i18n/messages/en'
import { useT } from '@/lib/i18n/translate'

/** Pieces the restaurant accounts page (E7) and the branch staff page (H8) share. */

export function Identity({ user }: { user: AccountIdentity }) {
  const name = accountName(user)
  return (
    <span className="flex min-w-0 flex-col">
      <span className="truncate font-medium">{name}</span>
      <span className="text-muted flex flex-wrap gap-x-3 text-sm">
        {user.phone && user.phone !== name && <span>{user.phone}</span>}
        {user.email && user.email !== name && <span>{user.email}</span>}
      </span>
    </span>
  )
}

/** A DELETE that needs a second press (PRD: keep destructive actions apart); `confirmOf` takes `{name}`. */
export function RevokeButton({
  path,
  queryKey,
  user,
  onRevoked,
  label,
  confirm,
  confirmOf,
}: {
  path: string
  queryKey: readonly unknown[]
  user: AccountIdentity
  onRevoked: () => void
  label: MessageKey
  confirm: MessageKey
  confirmOf: MessageKey
}) {
  const t = useT()
  const queryClient = useQueryClient()
  const revoke = useMutation({
    mutationFn: () => request<void>(path, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey })
      onRevoked()
    },
  })

  return (
    <div className="flex flex-col gap-2">
      <FormError error={revoke.error} />
      <ConfirmButton
        label={t(label)}
        confirmLabel={t(confirm)}
        confirmAriaLabel={t(confirmOf, { name: accountName(user) })}
        confirm={() => revoke.mutateAsync()}
      />
    </div>
  )
}
